import type { ContentType } from '@apuestapr/contracts';
import type { ReactElement } from 'react';
import { Route, Routes, useParams } from 'react-router-dom';

import { useSessionContext } from '../auth/SessionProvider.tsx';
import { dashboardContentCatalog, parseDashboardContentType } from './catalog.ts';
import { ContentFormPage } from './ContentFormPage.tsx';
import { ContentLayout } from './ContentLayout.tsx';
import { ContentFailureState, ContentListPage } from './ContentListPage.tsx';
import { ContentPreviewPage } from './ContentPreviewPage.tsx';
import { PageSeoFormPage, PageSeoListPage } from './PageSeoPages.tsx';
import {
  useContentDetail,
  useContentPreview,
  usePageSeoDetail,
  usePageSeoList,
  type RequestState,
} from './hooks.ts';

const idFrom = (value: string | undefined): number | null =>
  value !== undefined && /^[1-9]\d*$/.test(value) ? Number(value) : null;

const DataState = <Value,>({
  state,
  ready,
}: {
  readonly state: RequestState<Value>;
  readonly ready: (value: Value) => ReactElement;
}): ReactElement => {
  switch (state.status) {
    case 'loading':
      return <p role="status">Cargando contenido…</p>;
    case 'error':
      return <ContentFailureState failure={state.failure} />;
    case 'ready':
      return ready(state.data);
  }
};

const UnknownContentType = (): ReactElement => (
  <section>
    <h2>Tipo de contenido desconocido</h2>
    <p>La ruta no corresponde a uno de los seis tipos de contenido admitidos.</p>
  </section>
);

const KnownContentList = ({ type }: { readonly type: ContentType }): ReactElement => (
  <ContentListPage type={type} />
);

const ContentListRoute = (): ReactElement => {
  const type = parseDashboardContentType(useParams().type);
  return type === null ? <UnknownContentType /> : <KnownContentList type={type} />;
};

const ContentNewRoute = (): ReactElement => {
  const type = parseDashboardContentType(useParams().type);
  if (type === null) return <UnknownContentType />;
  return <ContentFormPage type={type} item={null} />;
};

const KnownContentEdit = ({
  type,
  id,
}: {
  readonly type: ContentType;
  readonly id: number;
}): ReactElement => {
  const { client, handleFailure } = useSessionContext();
  const state = useContentDetail(client, type, id, handleFailure);
  return <DataState state={state} ready={(item) => <ContentFormPage type={type} item={item} />} />;
};

const KnownContentPreview = ({
  type,
  id,
}: {
  readonly type: ContentType;
  readonly id: number;
}): ReactElement => {
  const { client, handleFailure } = useSessionContext();
  const state = useContentPreview(client, type, id, handleFailure);
  return (
    <section className="grid gap-4">
      <h2 className="sr-only">Vista previa · {dashboardContentCatalog[type].label}</h2>
      <DataState
        state={state}
        ready={(preview) => <ContentPreviewPage preview={preview} type={type} />}
      />
    </section>
  );
};

const ContentItemRoute = ({ preview }: { readonly preview: boolean }): ReactElement => {
  const parameters = useParams();
  const type = parseDashboardContentType(parameters.type);
  const id = idFrom(parameters.id);
  if (type === null) return <UnknownContentType />;
  if (id === null) return <UnknownContentType />;
  return preview ? (
    <KnownContentPreview type={type} id={id} />
  ) : (
    <KnownContentEdit type={type} id={id} />
  );
};

const PageSeoListRoute = (): ReactElement => {
  const session = useSessionContext();
  const authenticated = session.state.status === 'authenticated' ? session.state.session : null;
  const state = usePageSeoList(session.client, session.handleFailure);
  if (authenticated === null) return <></>;
  return (
    <DataState
      state={state}
      ready={(rows) => <PageSeoListPage initialRows={rows} role={authenticated.user.rol} />}
    />
  );
};

const PageSeoNewRoute = (): ReactElement => <PageSeoFormPage item={null} />;

const PageSeoEditRoute = (): ReactElement => {
  const id = idFrom(useParams().id);
  if (id === null) return <UnknownContentType />;
  return <KnownPageSeoDetail id={id} />;
};

const KnownPageSeoDetail = ({ id }: { readonly id: number }): ReactElement => {
  const { client, handleFailure } = useSessionContext();
  const state = usePageSeoDetail(client, id, handleFailure);
  return <DataState state={state} ready={(item) => <PageSeoFormPage item={item} />} />;
};

/** Static page-SEO routes are deliberately declared before generic `:type` routes. */
export const ContentRouteTree = (): ReactElement => (
  <Routes>
    <Route element={<ContentLayout />}>
      <Route path="page-seo" element={<PageSeoListRoute />} />
      <Route path="page-seo/new" element={<PageSeoNewRoute />} />
      <Route path="page-seo/:id/edit" element={<PageSeoEditRoute />} />
      <Route path=":type" element={<ContentListRoute />} />
      <Route path=":type/new" element={<ContentNewRoute />} />
      <Route path=":type/:id/edit" element={<ContentItemRoute preview={false} />} />
      <Route path=":type/:id/preview" element={<ContentItemRoute preview />} />
      <Route path="*" element={<UnknownContentType />} />
    </Route>
  </Routes>
);
